import { DirectoryItem, ActionResult, DirectoryItemCreate } from '../types'
import { useDirectoryTree } from '../useDirectoryTree'
import { ExecutableAction } from './action'
import { DeleteAction } from './deleteAction'

async function createDirectoryItem(item: DirectoryItemCreate): Promise<DirectoryItem> {
  return Promise.resolve({
    id: crypto.randomUUID(),
    createdDate: new Date().toISOString(),
    uploadedBy: 'user',
    ...item,
    ...(item.type === 'directory'
      ? {
          children: await Promise.all(
            (item.children ?? []).map((child) => createDirectoryItem(child))
          )
        }
      : {})
  } as DirectoryItem)
}

export class CreateNewAction extends ExecutableAction<DirectoryItem[], undefined> {
  static readonly actionName = 'create-new'
  public readonly actionName = CreateNewAction.actionName

  private createdItems: DirectoryItem[] = []

  constructor(
    private readonly items: DirectoryItemCreate[],
    private readonly parentId?: string,
    private readonly itemCreator: (
      item: DirectoryItemCreate
    ) => Promise<DirectoryItem> = createDirectoryItem
  ) {
    super()
  }

  override async execute(
    tree: ReturnType<typeof useDirectoryTree>
  ): Promise<ActionResult<DirectoryItem[]>> {
    const successfullyCreatedItems: DirectoryItem[] = []
    for (const item of this.items) {
      try {
        const newItem = await this.itemCreator(item)
        const newItemNode = tree.addNode(this.parentId ?? tree.getRootNode().id, newItem)
        if (newItemNode) {
          this.createdItems.push(newItem)
          successfullyCreatedItems.push(newItemNode)
        } else {
          return {
            status: 'error',
            message: `Failed to create ${item.type}`
          }
        }
      } catch (e) {
        const msg = `Failed to create ${item.type}`
        return {
          status: 'error',
          message: msg,
          error: e instanceof Error ? e : undefined
        }
      }
    }
    return {
      status: 'success',
      data: successfullyCreatedItems
    }
  }

  override async undo(tree): Promise<ActionResult> {
    return this.createdItems.length > 0
      ? new DeleteAction(this.createdItems).execute(tree)
      : {
          status: 'error',
          message: 'No item to undo creation for.'
        }
  }
}
